import type { AdminOrderDetailDto, AdminOrderPaymentDto } from '@carcheck/shared';
import LockIcon from '@mui/icons-material/Lock';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { observer } from 'mobx-react-lite';
import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { IssueRefundDialog, type IssueRefundResult } from './IssueRefundDialog';
import { useHasPermission } from '../../../auth';
import { useIssueRefund } from '../_lib/useIssueRefund';

/**
 * Issue-refund action block (AUT-129; extracted from `PaymentTab` in AUT-219).
 *
 * Rendered only when the payment exists, the admin holds `process_payouts`,
 * and there is a positive available-to-refund balance
 * (`payment.amount − payment.refundedTotal`). The backend stays the
 * refund-eligibility authority; this only gates the click.
 */
export const RefundAction = observer(
  ({
    order,
    payment,
  }: {
    order: AdminOrderDetailDto;
    payment: AdminOrderPaymentDto;
  }): ReactElement | null => {
    const { t } = useTranslation();
    const canRefund = useHasPermission('process_payouts');
    const [dialogOpen, setDialogOpen] = useState(false);
    const { busy, run } = useIssueRefund();

    const available = Math.max(Number(payment.amount) - Number(payment.refundedTotal), 0);
    if (!canRefund || available <= 0) return null;

    const onConfirm = (input: IssueRefundResult): void => {
      void run({
        paymentId: payment.id,
        body: {
          amount: input.amount,
          reason: input.reason,
          recipientId: order.customer.id,
          idempotencyKey: crypto.randomUUID(),
        },
      }).then((ok) => {
        if (ok) setDialogOpen(false);
      });
    };

    return (
      <Box>
        <Button
          variant="contained"
          color="error"
          startIcon={<LockIcon />}
          disabled={busy}
          onClick={() => setDialogOpen(true)}
        >
          {t('orders.actions.issueRefund')}
        </Button>
        <IssueRefundDialog
          open={dialogOpen}
          loading={busy}
          order={order}
          onConfirm={onConfirm}
          onCancel={() => setDialogOpen(false)}
        />
      </Box>
    );
  },
);
